// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import { type WeightUnit, getCurrencySymbol, getWeightUnitOptions } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '../../lib/base';
import type {
  CreateBlendProductMutation,
  CreateBlendProductMutationVariables,
  CreateCropProductMutation,
  CreateCropProductMutationVariables,
} from '../../types/__generated__/graphql';
import { shortenWeightUnit, validateNumInput } from '../../utils';
import { ExitDialogButton } from '../common/button';

const CREATE_CROP_PRODUCT_MUT = gql`
  mutation CreateCropProduct($crop_id: Int!, $size: Float!, $unit: String!, $price: Float) {
    createCropProduct(input: { crop_id: $crop_id, size: $size, unit: $unit, price: $price }) {
      updated_crop {
        id
        updated_at
      }
      product_created {
        id
      }
    }
  }
`;
const CREATE_BLEND_PRODUCT_MUT = gql`
  mutation CreateBlendProduct($blend_id: Int!, $size: Float!, $unit: String!, $price: Float) {
    createBlendProduct(input: { blend_id: $blend_id, size: $size, unit: $unit, price: $price }) {
      updated_blend {
        id
        updated_at
      }
      product_created {
        id
      }
    }
  }
`;

interface Props {
  readonly cropOrBlendId: number;
  readonly type: 'crop' | 'blend';
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export default function AddProductDialog({ isOpen, type, onClose, onSuccess, cropOrBlendId }: Props) {
  const [createCropProduct, { loading }] = useMutation<CreateCropProductMutation, CreateCropProductMutationVariables>(
    CREATE_CROP_PRODUCT_MUT,
  );
  const [createBlendProduct] = useMutation<CreateBlendProductMutation, CreateBlendProductMutationVariables>(
    CREATE_BLEND_PRODUCT_MUT,
  );
  const { preferredWeightUnit, currencyCode } = useUserContext();

  const [size, setSize] = React.useState('');
  const [unit, setUnit] = React.useState<WeightUnit>(preferredWeightUnit);
  const [price, setPrice] = React.useState('');
  const [sizeValidation, setSizeValidation] = React.useState('');
  const [priceValidation, setPriceValidation] = React.useState('');

  const onUnitChange = (e: SelectChangeEvent<string | null>) => {
    setUnit(e.target.value as WeightUnit);
  };
  const onSizeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      isRequired: true,
      validateGT: 0,
      setValidationMessage: (msg) => setSizeValidation(msg),
    });
    setSize(e.target.value);
  };
  const onSizeBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      isRequired: true,
      validateGT: 0,
      setValidationMessage: (msg) => setSizeValidation(msg),
    });
  };
  const onPriceChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      isRequired: false,
      validateGT: 0,
      decimals: 2,
      setValidationMessage: (msg) => setPriceValidation(msg),
    });
    setPrice(e.target.value);
  };
  const onPriceBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      isRequired: false,
      validateGT: 0,
      decimals: 2,
      setValidationMessage: (msg) => setPriceValidation(msg),
    });
  };

  const disableFields = loading;

  const onSubmit = () => {
    if (type === 'crop') {
      void createCropProduct({
        variables: {
          size: Number.parseFloat(size),
          unit: unit,
          price: price === '' ? null : Number.parseFloat(price),
          crop_id: cropOrBlendId,
        },
        onCompleted: (dataIn) => {
          if (dataIn !== undefined && dataIn.createCropProduct != null) {
            enqueueSnackbar('Created product', { variant: 'success' });
            onSuccess();
          } else {
            enqueueSnackbar('Error creating product, no data returned', { variant: 'error', autoHideDuration: 10000 });
          }
          setSize('');
          setUnit(preferredWeightUnit);
          setPrice('');
          setSizeValidation('');
          setPriceValidation('');
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          clientLogger.error({ title: 'edit_crop_error', message: errorIn.message });
        },
      });
    } else {
      void createBlendProduct({
        variables: {
          size: Number.parseFloat(size),
          unit: unit,
          price: price === '' ? null : Number.parseFloat(price),
          blend_id: cropOrBlendId,
        },
        onCompleted: (dataIn) => {
          if (dataIn !== undefined && dataIn.createBlendProduct != null) {
            enqueueSnackbar('Created product', { variant: 'success' });
            onSuccess();
          } else {
            enqueueSnackbar('Error creating product, no data returned', { variant: 'error', autoHideDuration: 10000 });
          }
          setSize('');
          setUnit(preferredWeightUnit);
          setPrice('');
          setSizeValidation('');
          setPriceValidation('');
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          clientLogger.error({ title: 'edit_crop_error', message: errorIn.message });
        },
      });
    }
  };

  const hasInputValidationError: boolean = priceValidation !== '' || sizeValidation !== '';
  const requiredFieldsFilled: boolean = size !== '';
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields || loading;

  const onCancelKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClose();
    }
  };
  const onSubmitKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (!isSubmitDisabled) {
        onSubmit();
      }
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add New Product</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>
        <Box sx={{ paddingTop: 1 }}>
          <Grid
            container
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
            alignItems="center"
            gap={2}
          >
            <Grid
              flexGrow="2"
              size={{ xs: 12, sm: 'grow' }}
              container
              display="flex"
              flexWrap="nowrap"
              gap={2}
              alignItems="center"
            >
              <Grid flexGrow="1">
                <TextField
                  aria-describedby="size"
                  id="size"
                  label="Size"
                  value={size}
                  onChange={onSizeChange}
                  onBlur={onSizeBlur}
                  hasSubtext
                  subtext={sizeValidation}
                  error={sizeValidation !== ''}
                  // onEnter={onEnter}
                  required
                  numberMin={0}
                  type="number"
                  inputMode="decimal"
                  disabled={disableFields}
                />
              </Grid>
              <Grid sx={{ minWidth: 'unset' }}>
                <FormControl fullWidth>
                  <InputLabel id="units-selector">Units</InputLabel>
                  <Select
                    aria-describedby="units-selector"
                    id="units-selector"
                    labelId="units-selector"
                    label="Units"
                    value={unit}
                    onChange={onUnitChange}
                    // onEnter={onEnter}
                    required={true}
                    autoFocus={false}
                    disabled={disableFields}
                  >
                    {getWeightUnitOptions(preferredWeightUnit).map((sizeUnit) => {
                      return (
                        <MenuItem key={sizeUnit} value={sizeUnit}>
                          {shortenWeightUnit(sizeUnit)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid flexGrow="1" size={{ xs: 12, sm: 'auto' }}>
              <TextField
                aria-describedby="price"
                id="price"
                label="Price (optional)"
                value={price}
                onChange={onPriceChange}
                onBlur={onPriceBlur}
                startAdornment={<span style={{ marginRight: 4 }}>{getCurrencySymbol(currencyCode)}</span>}
                hasSubtext
                subtext={priceValidation}
                error={priceValidation !== ''}
                type="number"
                inputMode="decimal"
                numberMin={0}
                numberStep={0.01}
                disabled={disableFields}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          onKeyDown={onCancelKeyDown}
        >
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={onSubmitKeyDown}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
